import { Col, Form, Input, InputNumber, Row, Select } from "antd"
import { useEffect, useMemo, useRef } from "react"

import { SFormHeader } from "#blocks/Form/formComponents"
import { NULL_ID } from "#classes/Firebase"
import DisabledFieldTooltip from "#components/buildingBlocks/Form/DisabledFieldTooltip"
import SemesterDropdown from "#components/forms/SemesterDropdown"
import SkillLevelDropdown from "#components/forms/SkillLevelDropdown"
import { CLASS_GROUP_OPTIONS } from "#constants/classOptions"
import { EVAL_TYPE_BY_CLASS_GROUP } from "#constants/skillLevelConstants"
import { type AddCurriculumFormValues } from "#customTypes/composedTypes/curriculumTypes"
import {
  COMPARATORS,
  DOC_TYPES,
} from "#customTypes/composedTypes/firebaseTypes"
import { CheckUsageQuery, useCheckUsage } from "#hooks/reactQuery/useCheckUsage"
import { filterFormOptions } from "#utils/formHelpers"

import ProjectAssignmentForm from "./ProjectAssignmentForm"

const AddCurriculumForm = ({
  initialValues,
}: {
  initialValues: AddCurriculumFormValues
}) => {
  const form = Form.useFormInstance<AddCurriculumFormValues>()
  const classGroup = Form.useWatch("group", form) ?? initialValues?.group
  const prevGroupValRef = useRef(classGroup)

  const curriculumId = initialValues?.id || NULL_ID
  const queries: CheckUsageQuery[] = useMemo(
    () => [
      {
        docType: DOC_TYPES.CLASSES,
        queries: {
          curriculumId: {
            value: curriculumId,
            comparator: COMPARATORS.EQUALS,
          },
        },
      },
    ],
    [curriculumId]
  )

  const { data: isUsed } = useCheckUsage(
    DOC_TYPES.CURRICULUMS,
    curriculumId,
    queries
  )

  // Clear skill levels when the group changes between digital and rest
  useEffect(() => {
    const prevGroup = EVAL_TYPE_BY_CLASS_GROUP[prevGroupValRef.current!]
    const newGroup = EVAL_TYPE_BY_CLASS_GROUP[classGroup!]

    if (prevGroup !== newGroup) {
      form.setFieldValue("skillLevels", [])
    }
    prevGroupValRef.current = classGroup
  }, [classGroup, form])

  return (
    <>
      <Row gutter={16}>
        <Col span={14}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Name"
                rules={[
                  {
                    required: true,
                    message: "Please input the curriculum plan name!",
                  },
                ]}
              >
                <Input placeholder="Enter curriculum plan name" />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item
                name="group"
                label="Class group"
                rules={[
                  { required: true, message: "Please select a class group!" },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select a group"
                  filterOption={filterFormOptions}
                  options={CLASS_GROUP_OPTIONS}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <SemesterDropdown
                disabledTooltip={
                  isUsed
                    ? "Cannot change semester - this curriculum is currently being used by classes"
                    : undefined
                }
              />
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <SkillLevelDropdown multiSelect classGroup={classGroup} />
            </Col>
            <Col span={6}>
              <Form.Item
                name="cycle"
                label="Cycle"
                rules={[{ required: true, message: "Please input the cycle!" }]}
              >
                <Input placeholder="Enter cycle" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <DisabledFieldTooltip
                disabled={isUsed}
                tooltip={
                  isUsed
                    ? "Cannot change number of sessions - this curriculum is currently being used by classes"
                    : undefined
                }
              >
                <Form.Item
                  name="numberOfSessions"
                  label="Number of sessions"
                  rules={[
                    {
                      required: true,
                      message: "Please input the number of sessions!",
                    },
                  ]}
                >
                  <InputNumber
                    value={initialValues?.numberOfSessions}
                    style={{ width: "100%" }}
                    placeholder="Number of sessions"
                    min={0}
                    max={365}
                    disabled={isUsed}
                  />
                </Form.Item>
              </DisabledFieldTooltip>
            </Col>
          </Row>
        </Col>
        <Col span={10}>
          <Form.Item name="notes" label="Notes" rules={[{ required: false }]}>
            <Input.TextArea placeholder="Enter any notes" rows={5} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          <SFormHeader>Projects</SFormHeader>
          <ProjectAssignmentForm />
        </Col>
      </Row>

      <Form.Item name="id" style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
    </>
  )
}

export default AddCurriculumForm
